import { config } from '@abyss/web/tools/config';
import { storage } from '@abyss/web/tools/storage';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import React, { Fragment, useContext, useEffect } from 'react';

import { PopulationType } from '../../../../../../api/src/services/Constants';
import { useGuestStore } from '../../../../../../parcels/src/store/useGuestStore';
import packageJson from '../../../../../package.json';
import { Constants } from '../../../../common/Constants';
import { isIfpMember } from '../../../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../../../context/PortalContext';
import { useLagoon } from '../../../../hooks/useLagoon';
import { getBlueGreenEnv } from '../../../../utils/cookies.utils';
import {
  getCoverageTypes,
  getCurrentMember,
} from '../../../../utils/user.utils';
import { IChildrenProp } from '../../types';
import { getPlanSelectorVariables } from '../PlanSelector/helpers';

const defaultDomainExclusions = [
  'lpsnmedia.net(\\/[^]*)?$',
  'iperceptions.com(\\/[^]*)?$',
  'azureedge.net(\\/[^]*)?$',
  'adobedtm.com(\\/[^]*)?$',
  'async-msg[^.]*\\.optum\\.com(\\/[^]*)?$',
  'cloudfront.net(\\/[^]*)?$',
  'doubleclick.net(\\/[^]*)?$',
  'liveperson.net(\\/[^]*)?$',
  'optumtrax\\.optum\\.com(\\/[^]*)?$',
  'qualtrics.com(\\/[^]*)?$',
  'smetrics\\.uhc\\.com(\\/[^]*)?$',
  'omtrdc.net(\\/[^]*)?$',
  'ometrics\\.optum\\.com(\\/[^]*)?$',
  'sessioncam.com(\\/[^]*)?$',
  'facebook.com(\\/[^]*)?$',
  'google.com(\\/[^]*)?$',
  'cloudflare.com(\\/[^]*)?$',
  'abyss-cloud\\.uhc\\.com(\\/[^]*)?$',
  'nextportal-cdn.prod.werally.in(\\/[^]*)?$',
  'mapbox.com(\\/[^]*)?$',
];

export const useConfig = (key: string): string => {
  const allConfigs = useLagoon('config');
  const configs = allConfigs(key);
  return configs ? configs.value : '';
};

export const useConfigs = (keys: string[]): string[] => {
  const allConfigs = useLagoon('config');
  return keys.map((key) => {
    const config = allConfigs(key);
    return config ? config.value : '';
  });
};

const generateNewGlobalContext = (
  currentMember,
  uniqueMemberId,
  envVar,
  portalData
) => {
  const isIFPLineOfBusiness =
    isIfpMember(currentMember?.population) ||
    currentMember?.population === PopulationType.IFP_BINDER;
  let newGlobalContext = {
    lineOfBusiness: isIFPLineOfBusiness
      ? PopulationType.IFP
      : currentMember?.lineOfBusiness?.replace(/&/g, 'n'),
    groupId: currentMember?.policyNumber,
    hsid: uniqueMemberId,
    membershipCategory: currentMember?.membershipCategory || '',
    population: currentMember?.population || '',
    bgEnv: envVar,
    planName:
      currentMember?.planDetail?.eligibility?.benefitSet?.find(
        (benefitSet) => benefitSet.coverageTypeCode.code === 'M'
      )?.planNameType?.brandingLongName || '', // from OBAPI's planDetails response for medical coverage
    coverageTypeCode: getCoverageTypes(currentMember)?.includes('M')
      ? 'M'
      : currentMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType[0]
          .typeCode.coverageTypeCode, // if member has medical coverage, otherwise first coverage returned from obapi's profile
    portal: `${portalData?.portalName ?? ''}${
      portalData?.portalIdentifier ? '_' + portalData?.portalIdentifier : ''
    }`,
  };

  if (currentMember?.isIFPEarlyPay) {
    newGlobalContext = {
      ...newGlobalContext,
      portal: `ifp-earlypay${
        portalData?.portalIdentifier ? '_' + portalData?.portalIdentifier : ''
      }`,
    };
  }

  return newGlobalContext;
};

export const Analytics = ({ children }: IChildrenProp) => {
  let envVar = config('ENV_VAR');
  const apiGateway = config('AWS_API_GATEWAY');
  const applicationId = useConfig('DATADOG_EXT_APPLICATION_ID');
  const clientToken = useConfig('DATADOG_EXT_CLIENT_TOKEN');
  const service = useConfig('DATADOG_EXT_SERVICE');
  const currentMember = getCurrentMember();
  const { portalData } = useContext(PortalContext);
  const { accountId } = getPlanSelectorVariables();
  const { guestData } = useGuestStore((state) => ({
    guestData: state.guestData,
  }));

  const uniqueMemberId =
    guestData?.exchangeId ||
    accountId ||
    portalData?.portalEligibilityData?.uniqueMemberId ||
    '';

  // Appending Blue/Green Env for logging Goes in Analytics function
  const host = window.location.hostname;
  const bgEnv = getBlueGreenEnv(host);
  if (bgEnv) {
    envVar += `-${bgEnv}`;
  }

  useEffect(() => {
    if (envVar === 'local') return;

    if (!applicationId || !clientToken || !service) {
      // eslint-disable-next-line no-console, consistent-return
      return console.warn(
        'DataDog init failure due to incomplete receive applicationId, clientToken, service!'
      );
    }

    datadogLogs.init({
      clientToken,
      site: 'datadoghq.com',
      service,
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      env: envVar,
      version: packageJson.version,
    });

    const excludedActivityPattern = new RegExp(
      `^https:\/\/((www|beta)\\.)?(${defaultDomainExclusions.join('|')})`
    );

    const eniGlobalNavPattern = new RegExp('eni-global-nav');

    datadogRum.init({
      applicationId,
      clientToken,
      site: 'datadoghq.com',
      service,
      env: envVar,
      version: packageJson.version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackFrustrations: true,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      traceSampleRate: 100,
      allowedTracingUrls: [
        `${window.location.protocol}//${window.location.host}`,
        apiGateway as string,
      ],
      excludedActivityUrls: [
        excludedActivityPattern,
        eniGlobalNavPattern,
        'https://member.uhc.com/findcare/api/graphql?q=PrimaryCare',
        'https://member.uhc.com/ce/api/globalnav/graphql',
        'https://api.member.uhc.com/benefits/eni/graphql?traceName=global-nav',
      ],
      storeContextsAcrossPages: true,
      defaultPrivacyLevel: 'mask-user-input',
    });
    const newGlobalContext = generateNewGlobalContext(
      currentMember,
      uniqueMemberId,
      envVar,
      portalData
    );

    datadogRum.setGlobalContext(newGlobalContext);
    datadogRum.setUser({ id: accountId ?? '' });
    datadogRum.startSessionReplayRecording();
  }, [applicationId, clientToken, service]);

  useEffect(() => {
    const dataDogGlobalContext = datadogRum.getGlobalContext();
    const { ACTIVE_MEMBER_INDEX } = Constants.STORAGE_KEYS.SESSION;
    const currentIndex = Number(storage.session.get(ACTIVE_MEMBER_INDEX));

    const isDataDogGlobalContextEmpty = !dataDogGlobalContext?.coverageTypeCode;

    const newGlobalContext = generateNewGlobalContext(
      currentMember,
      uniqueMemberId,
      envVar,
      portalData
    );

    if (
      (JSON.stringify(dataDogGlobalContext) ===
        JSON.stringify(newGlobalContext) ||
        currentIndex !== 0) &&
      !isDataDogGlobalContextEmpty
    ) {
      return;
    } else {
      datadogRum.setGlobalContext(newGlobalContext);
    }
  }, [currentMember]);

  return <Fragment>{children}</Fragment>;
};
